import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
  Avatar,
  Box,
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Modal,
  Skeleton,
  Stack,
  SvgIcon,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Container } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers';
import { useFormik } from 'formik';
import moment from 'moment';
import { useEffect, useState, type HTMLAttributes, type InputHTMLAttributes } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import SucessModal from 'src/components/sucess-modal';
import PayerConfirmationModal from 'src/components/common-modals/payer-confirmation-modal';
import { eocFormTestId, eocOptionTestId } from 'src/components/common-modals/test-ids';
import { appointmentBookFlag } from 'src/config';
import useCurrentUser from 'src/hooks/use-current-user';
import { useFeatureToggleWhenReady } from 'src/hooks/use-feature-toggle';
import { useSettings } from 'src/hooks/use-settings';
import SimpleSnackbar from 'src/pages/details-page/sub-components/snackBar';
import { paths } from 'src/paths';
import { getPatientIdAction } from 'src/redux/actions/patientAction';
import {
  createEpisodeOfCare,
  editEpisodeOfCare,
  resetCreateEpisodeOfCare,
  resetEditEpisodeOfCare,
} from 'src/redux/actions/patientEpisodeOfCare';
import { parseDateWithoutTimezone } from 'src/utils/convertDateToSelectedTimeZone';
import * as Yup from 'yup';
import { disciplineType } from 'src/redux/actions/patientScreening';

const { patientDetails, episodeOfCare, patients, details, payers } = paths;

function getCurrentDateUTCWithoutTime(date: any) {
  // Get the date components
  const utcDateFormate = moment(date).format('YYYY-MM-DD');
  // Construct the UTC date string with time portion set to 00:00:00.000Z
  const utcDateString = `${utcDateFormate}T00:00:00.000Z`;

  return utcDateString;
}

const AddNewEpisodeOfCare = ({ action }: any) => {
  const navigate = useNavigate();
  const settings = useSettings();
  const [file, setFile] = useState<any>(null);
  const { state } = useLocation();
  const episodeOfCareId = state?.episodeOfCareId;
  const isMultiPayer = state?.isMultiPayer;
  const episodeOfCareData = useSelector((state: any) => state?.getSingleEpisodeOfCare?.response);
  const getPatientId = useSelector((state: any) => state?.getPatientIdReducer);
  const createSpisodOfCare = useSelector((state: any) => state?.createEpisodeOfCareReducer);
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const editSpisodOfCare = useSelector((state: any) => state?.editEpisodeOfCareReducer);
  const [providerId, timeZone] = useCurrentUser(['providerId', 'timeZone']);
  const disciplineTypeOption = useSelector(
    (state: any) => state.getDisciplineTypeReducer?.response
  );
  const dispatch = useDispatch();
  const [openErrorModal, setOpenErrorModal] = useState(false);
  const [payerPlans, setPayerPlans] = useState([]);
  const [selectedPayerPlan, setSelectedPayerPlan] = useState<any>('');
  const [openPayerConfirmationModal, setOpenPayerConfirmationModal] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<any>(null);
  // Place-of-Residence change confirmation (SNF ↔ Outpatient) — only relevant
  // when the appointment book is enabled, since that's what owns the scheduler
  // tasks the reset clears.
  const [isAppointmentBook] = useFeatureToggleWhenReady(appointmentBookFlag, 'FACILITY');
  const [openResidenceConfirmModal, setOpenResidenceConfirmModal] = useState(false);
  const [pendingResidenceEditPayload, setPendingResidenceEditPayload] = useState<any>(null);
  const getPayerPlanTypeResponse = useSelector(
    (state: any) => state.getPayerPlanTypeReducer?.enterprisePayerPlanType?.data
  );
  const patientLoading = useSelector((state: any) => state.getSinglePatientReducer.loading);
  const getAllPatientPayersReducer = useSelector((state: any) => state?.getAllPatientPayersReducer);

  const saveToLocalStorage = (key: any, value: any) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const getFromLocalStorage = (key: any) => {
    const storedValue = localStorage.getItem(key);
    if (storedValue !== undefined && storedValue !== null) {
      try {
        return JSON.parse(storedValue);
      } catch (error) {
        return null;
      }
    }
    return null;
  };

  const backToEocDetails = () => {
    navigate(`${patients}/${patientDetails}${episodeOfCare}${details}`, {
      state: {
        episodeOfCareId: episodeOfCareId,
        getPatientId,
      },
    });
  };

  useEffect(() => {
    saveToLocalStorage('disciplineOfComplete', episodeOfCareData?.disciplineOfComplete);
    saveToLocalStorage('payerType', episodeOfCareData?.payerType);
    saveToLocalStorage('timeZone', timeZone);

    if (episodeOfCareData?.payerPlanEntity) {
      const modifiedPayerPlanName = episodeOfCareData?.payerPlanEntity?.payerPlanName;
      saveToLocalStorage('payerPlanEntity', modifiedPayerPlanName);
    }
  }, [episodeOfCareData, providerId, payerPlans]);

  const initialDisciplineValue = getFromLocalStorage('disciplineOfComplete') || '';
  const initialPayerTypeValue = getFromLocalStorage('payerType') || '';
  const initialPayerPlanName = getFromLocalStorage('payerPlanEntity') || '';
  const startDateWithoutTimeformat =
    episodeOfCareData?.startDate && episodeOfCareData?.startDate?.split('T')?.at(0);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      episodeTitle: action === 'Edit' ? episodeOfCareData?.episodeTitle : '',
      disciplineOfComplete: action === 'Edit' ? initialDisciplineValue : '',
      startDate:
        action === 'Edit'
          ? startDateWithoutTimeformat
            ? new Date(
                new Date(startDateWithoutTimeformat).valueOf() +
                  new Date(startDateWithoutTimeformat).getTimezoneOffset() * 60 * 1000
              )
            : null
          : null,
      endDate:
        action === 'Edit' ? (episodeOfCareData?.endDate ? episodeOfCareData?.endDate : null) : null,
      medicalRecordNumber: patientData?.medicalRecordNumber ? patientData?.medicalRecordNumber : '',
      payerType: action === 'Edit' ? initialPayerTypeValue : '',
      payerPlanEntity: action === 'Edit' ? initialPayerPlanName : '',
      payerPlanStartDate:
        action === 'Edit'
          ? episodeOfCareData?.payerPlanStartDate
            ? new Date(
                new Date(episodeOfCareData?.payerPlanStartDate).valueOf() +
                  new Date(episodeOfCareData?.payerPlanStartDate).getTimezoneOffset() * 60 * 1000
              )
            : null
          : null,
      payerPlanEndDate:
        action === 'Edit'
          ? episodeOfCareData?.payerPlanEndDate
            ? new Date(
                new Date(episodeOfCareData?.payerPlanEndDate).valueOf() +
                  new Date(episodeOfCareData?.payerPlanEndDate).getTimezoneOffset() * 60 * 1000
              )
            : null
          : null,
      isNonTherapyPatient: episodeOfCareData?.isNonTherapyPatient
        ? episodeOfCareData?.isNonTherapyPatient
        : false,
    },
    validationSchema: Yup.object({
      episodeTitle: Yup.string().max(255).required('Episode title is required'),
      disciplineOfComplete: Yup.string().max(255).required('Discipline Of Complete is required'),
      startDate: Yup.date()
        .required()
        .test(
          'eoc-start-date',
          'Episode Start Date must be after the patient’s Date of Birth',
          function (value) {
            if (!value) return false;
            const selectedEoc = moment(value).format();
            const formattedEoc = selectedEoc?.split('T')[0];
            const formattedDoB = patientData?.dateOfBirth?.split('T')[0];
            return formattedEoc > formattedDoB;
          }
        )
        .test('eoc-start-date-1', 'Episode Start Date cannot be in the future.', function (value) {
          if (!value) return false;
          const selectedEoc = moment(value).format();
          const currentDate = new Date();
          const tomorrowDate = new Date(currentDate);
          tomorrowDate.setDate(currentDate.getDate() + 1);
          const formattedTomorrowDate = moment(tomorrowDate).format()?.split('T')[0];
          return formattedTomorrowDate > selectedEoc;
        }),
      endDate: Yup.date().nullable(),
      medicalRecordNumber: Yup.string().required('Medical Record Number is required'),
      payerType: isMultiPayer ? Yup.string() : Yup.string().required('Payer Type is required'),
      payerPlanEntity: isMultiPayer ? Yup.string() : Yup.string().required('Payer is required'),
      payerPlanStartDate: Yup.date().nullable(),
      payerPlanEndDate: Yup.date().nullable(),
      isNonTherapyPatient: Yup.boolean(),
    }),
    onSubmit: async (values, helpers): Promise<void> => {
      setOpenErrorModal(false);
      const selectedPayerType = getPayerPlanTypeResponse?.find(
        (option: any) => option.displayName === values.payerType
      );
      const payerTypeLabel = selectedPayerType ? selectedPayerType.key : '';

      const startDate = getCurrentDateUTCWithoutTime(values?.startDate || new Date());
      const endDate = values?.payerPlanEndDate
        ? getCurrentDateUTCWithoutTime(values?.payerPlanEndDate)
        : null;

      if (action === 'Create') {
        // Check if payerType is null - show "no payer found" modal
        if (!patientData?.payerType) {
          setPendingFormValues({
            values,
            selectedPayerType,
            payerTypeLabel,
            startDate,
            endDate,
            hasNoPayer: true,
          });
          setOpenPayerConfirmationModal(true);
          return;
        }

        // Check if patient has a payer and show confirmation modal
        if (patientData?.payerType && patientData?.payerName) {
          setPendingFormValues({
            values,
            selectedPayerType,
            payerTypeLabel,
            startDate,
            endDate,
            hasNoPayer: false,
          });
          setOpenPayerConfirmationModal(true);
          return;
        }

        // Check if EOC start date is after payer end date
        if (getAllPatientPayersReducer?.data?.length !== 0) {
          const eocStartDate = moment(startDate.split('T')[0]);
          const payerEndDate = moment(getAllPatientPayersReducer?.data[0]?.endDate);

          if (eocStartDate.isAfter(payerEndDate)) {
            setPendingFormValues({
              values,
              selectedPayerType,
              payerTypeLabel,
              startDate,
              endDate,
              hasNoPayer: true,
            });
            setOpenPayerConfirmationModal(true);
            return;
          }
        }

        // If no payer check needed, proceed with creation (existing flow)
        dispatch(
          createEpisodeOfCare({
            ...values,
            startDate: startDate,
            patientId: getPatientId,
            payerPlanEndDate: null,
            payerPlanStartDate: null,
            payerType: payerTypeLabel,
            payerPlanEntity: isMultiPayer ? null : selectedPayerPlan,
            endDate: null,
          })
        );
      } else {
        const editPayload = {
          ...values,
          startDate: startDate,
          episodeOfCareId,
          payerPlanEndDate: endDate,
          payerPlanStartDate: startDate,
          payerType: payerTypeLabel,
          payerPlanEntity: selectedPayerPlan,
          endDate: values?.endDate,
        };
        // If Place of Residence flipped between SNF and Outpatient, warn the
        // user before submitting — this reset will clear planned minutes,
        // scheduled tasks, and the anticipated discharge date.
        const previousDiscipline = episodeOfCareData?.disciplineOfComplete;
        const nextDiscipline = values?.disciplineOfComplete;
        const isResidenceFlip =
          (previousDiscipline === 'SKILLED_NURSING_FACILITY' && nextDiscipline === 'OUTPATIENT') ||
          (previousDiscipline === 'OUTPATIENT' && nextDiscipline === 'SKILLED_NURSING_FACILITY');
        if (isResidenceFlip && isAppointmentBook) {
          setPendingResidenceEditPayload(editPayload);
          setOpenResidenceConfirmModal(true);
          return;
        }
        dispatch(editEpisodeOfCare(editPayload));
      }
    },
  });

  useEffect(() => {
    if (action === 'Edit' && !!episodeOfCareData) {
      return setSelectedPayerPlan(episodeOfCareData?.payerPlanEntity);
    }
  }, [episodeOfCareData]);

  useEffect(() => {
    if (!patientData) return;
    setPayerPlans(patientData?.facilityEntity?.payerPlans);
  }, [patientData]);

  const createEocRedirect = () => {
    dispatch(getPatientIdAction(getPatientId));
    navigate(`${patients}/${patientDetails}${episodeOfCare}`);
  };

  useEffect(() => {
    if (!createSpisodOfCare.isCreated && createSpisodOfCare.response === null) {
      setOpenErrorModal(false);
      return;
    }
    if (!createSpisodOfCare.isCreated && createSpisodOfCare.response !== null) {
      setOpenErrorModal(true);
      return;
    }
    if (createSpisodOfCare?.isCreated) setOpenErrorModal(false);

    setTimeout(() => {
      dispatch(resetCreateEpisodeOfCare());
      createEocRedirect();
    }, 1500);
    return;
  }, [createSpisodOfCare]);

  useEffect(() => {
    if (!editSpisodOfCare.isEdited && editSpisodOfCare.response === null) return;
    if (!editSpisodOfCare.isEdited && editSpisodOfCare.response !== null) {
      setOpenErrorModal(true);
      return;
    }
    if (editSpisodOfCare?.isEdited) setOpenErrorModal(false);
    setTimeout(() => {
      dispatch(resetEditEpisodeOfCare());
      backToEocDetails();
    }, 1500);
    return;
  }, [editSpisodOfCare]);

  const getFilterPayerPlans: any = (selectPayerType: any, payerPlans: any) => {
    const getList: any = getPayerPlanTypeResponse?.find(
      (option: any) => option.displayName === selectPayerType
    );

    if (!payerPlans || !selectPayerType) {
      return formik.values.payerPlanEntity !== '' ? [formik.values.payerPlanEntity] : [];
    }

    const resultList = payerPlans?.filter((item: any) => item?.payerPlanType === getList?.key);

    const isPayerPlanEntityIncluded = resultList?.some(
      (payer: any) => payer?.payerPlanName === formik.values.payerPlanEntity
    );

    if (!isPayerPlanEntityIncluded && formik.values.payerPlanEntity !== '') {
      resultList.unshift({ payerPlanName: formik.values.payerPlanEntity });
    }

    return resultList;
  };

  const closeErrorModal = () => {
    dispatch(resetCreateEpisodeOfCare());
    setOpenErrorModal(false);
  };

  const handleConfirmPayer = () => {
    if (!pendingFormValues) return;

    const { values, payerTypeLabel } = pendingFormValues;
    setOpenPayerConfirmationModal(false);

    dispatch(
      createEpisodeOfCare({
        ...values,
        startDate: pendingFormValues.startDate,
        patientId: getPatientId,
        payerPlanEndDate: null,
        payerPlanStartDate: null,
        payerType: payerTypeLabel,
        payerPlanEntity: isMultiPayer ? null : selectedPayerPlan,
        endDate: null,
      })
    );

    setPendingFormValues(null);
  };

  const handleUpdatePayer = () => {
    setOpenPayerConfirmationModal(false);
    setPendingFormValues(null);
    navigate(`${patients}/${patientDetails}${payers}`, {
      state: {
        getPatientId: getPatientId,
        from: 'EOC',
      },
    });
  };

  const getPayerTypeDisplayName = () => {
    if (!patientData?.payerType) return '';
    const payerTypeOption = getPayerPlanTypeResponse?.find(
      (option: any) => option.key === patientData.payerType
    );
    return payerTypeOption?.displayName || patientData.payerType;
  };

  useEffect(() => {
    dispatch(disciplineType({ patientId: getPatientId, episodeOfCareId }));
  }, [getPatientId, episodeOfCareId]);

  return (
    <Container maxWidth={settings.stretch ? false : 'xl'}>
      <Stack gap="1.5rem">
        <Button
          sx={{
            justifyContent: 'flex-start',
            color: '#111927',
            width: 'max-content',
            padding: '0px',
          }}
          onClick={() =>
            navigate(`${patients}/${patientDetails}${episodeOfCare}`, {
              state,
            })
          }
          startIcon={
            <SvgIcon>
              <ArrowBackIcon sx={{ color: '#6C737F' }} />
            </SvgIcon>
          }
        >
          <Typography variant="subtitle1">Back to Episode Of Care List</Typography>
        </Button>
        {patientLoading ? (
          <Stack>
            <Box sx={{ display: 'flex', width: '100%', alignItems: 'center', gap: 1 }}>
              <Skeleton
                animation="pulse"
                variant="circular"
              >
                <Avatar sx={{ width: 70, height: 70 }} />
              </Skeleton>
              <Box sx={{ width: 100 }}>
                <Typography
                  width={'100%'}
                  variant="h5"
                >
                  <Skeleton animation="pulse" />
                </Typography>
                <Typography
                  width={'30%'}
                  variant="h5"
                >
                  <Skeleton animation="pulse" />
                </Typography>
              </Box>
            </Box>
          </Stack>
        ) : (
          <Stack
            flexDirection="row"
            gap="1rem"
            alignItems="center"
          >
            <Box>
              <Avatar
                sx={{
                  height: 64,
                  width: 64,
                  border: '1px solid black',
                }}
              >
                <label
                  htmlFor="file"
                  style={{
                    width: '100%',
                    display: 'flex',
                    height: '100%',
                    borderRadius: '50%',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {file && (
                    <img
                      width="100%"
                      height="100%"
                      src={URL.createObjectURL(file)}
                      alt=""
                    />
                  )}
                  {!file && 'E'}
                </label>
              </Avatar>
            </Box>
            <input
              type="file"
              accept="image/*"
              onChange={(event) => setFile(event.target.files && event.target.files[0])}
              id="file"
              style={{ display: 'none', position: 'absolute' }}
            />{' '}
            <Typography variant="h4">
              {action === 'Edit'
                ? `${patientData?.lastName} ${patientData?.firstName} - Edit Episode Of Care`
                : `${patientData?.lastName} ${patientData?.firstName} - Add new Episode Of Care`}
            </Typography>
          </Stack>
        )}

        <form
          style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}
          onSubmit={formik.handleSubmit}
        >
          <Card sx={{ padding: '1.5rem' }}>
            <Stack gap="1rem">
              <Typography variant="h6">
                {action === 'Edit' ? 'Edit Basic Details' : 'Add Basic Details'}
              </Typography>
              <Grid
                spacing={3}
                container
              >
                <Grid
                  item
                  xs={6}
                >
                  <TextField
                    fullWidth
                    label="Episode Title"
                    error={!!(formik.touched.episodeTitle && formik.errors.episodeTitle)}
                    helperText={formik.touched.episodeTitle && (formik.errors.episodeTitle as any)}
                    name="episodeTitle"
                    inputProps={{ 'data-testid': eocFormTestId('episode-title') }}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.episodeTitle}
                    InputLabelProps={{
                      shrink:
                        formik.values.episodeTitle !== '' && !formik.touched.episodeTitle
                          ? true
                          : undefined,
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={6}
                >
                  <TextField
                    defaultValue={'SNF'}
                    fullWidth
                    label="Place of Residence"
                    error={
                      !!(formik.touched.disciplineOfComplete && formik.errors.disciplineOfComplete)
                    }
                    helperText={
                      formik.touched.disciplineOfComplete &&
                      (formik.errors.disciplineOfComplete as any)
                    }
                    name="disciplineOfComplete"
                    onBlur={formik.handleBlur}
                    onChange={(e) => {
                      const trimmedValue = e.target.value;

                      formik.setFieldValue('disciplineOfComplete', trimmedValue);
                    }}
                    value={formik.values.disciplineOfComplete}
                    select
                    SelectProps={{
                      SelectDisplayProps: {
                        'data-testid': eocFormTestId('place-of-residence'),
                      } as HTMLAttributes<HTMLDivElement>,
                    }}
                  >
                    {disciplineTypeOption?.map((option: any) => (
                      <MenuItem
                        key={option.key}
                        value={option.key}
                        data-testid={eocOptionTestId(
                          eocFormTestId('place-of-residence'),
                          option.key
                        )}
                      >
                        {option.displayName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid
                  item
                  xs={6}
                >
                  <DatePicker
                    onChange={(newDate) => {
                      formik.setFieldValue('startDate', newDate);
                      if (action === 'Edit') formik.setFieldValue('payerPlanStartDate', newDate);
                    }}
                    label="Start Date"
                    value={formik.values.startDate}
                    slotProps={{
                      textField: { inputProps: { 'data-testid': eocFormTestId('start-date') } },
                    }}
                    format="MM dd, yyyy"
                    sx={{
                      width: '100%',
                      border:
                        formik.touched.startDate && formik.errors.startDate ? '2px solid red' : '',
                      borderRadius:
                        formik.touched.startDate && formik.errors.startDate ? '10px' : '',
                    }}
                    minDate={
                      action === 'Edit' && !!episodeOfCareData?.minStart
                        ? parseDateWithoutTimezone(episodeOfCareData?.minStart?.toString())
                        : null
                    }
                    maxDate={
                      action === 'Edit' && !!episodeOfCareData?.maxStart
                        ? parseDateWithoutTimezone(episodeOfCareData?.maxStart?.toString())
                        : new Date()
                    }
                  />
                  {formik.touched.startDate && formik.errors.startDate && (
                    <Typography
                      padding={'0px 16px'}
                      color={'red'}
                      variant="caption"
                    >
                      {formik.errors.startDate}
                    </Typography>
                  )}
                </Grid>
                <Grid
                  item
                  xs={6}
                >
                  <TextField
                    disabled={patientData?.medicalRecordNumber ? true : false}
                    fullWidth
                    label="Medical Record Number"
                    error={
                      !!(formik.touched.medicalRecordNumber && formik.errors.medicalRecordNumber)
                    }
                    helperText={
                      formik.touched.medicalRecordNumber &&
                      (formik.errors.medicalRecordNumber as any)
                    }
                    name="medicalRecordNumber"
                    inputProps={{ 'data-testid': eocFormTestId('medical-record-number') }}
                    onBlur={formik.handleBlur}
                    onChange={formik.handleChange}
                    value={formik.values.medicalRecordNumber}
                    InputLabelProps={{
                      shrink:
                        formik.values.medicalRecordNumber !== '' &&
                        !formik.touched.medicalRecordNumber
                          ? true
                          : undefined,
                    }}
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={formik.values.isNonTherapyPatient}
                        onChange={(e) =>
                          formik.setFieldValue('isNonTherapyPatient', e.target.checked)
                        }
                        name="isNonTherapyPatient"
                        color="primary"
                        inputProps={
                          {
                            'data-testid': eocFormTestId('non-therapy-patient'),
                          } as InputHTMLAttributes<HTMLInputElement>
                        }
                      />
                    }
                    label={
                      <>
                        <Typography variant="body2">
                          Mark as non-therapy patient
                          <Tooltip title="Patient that doesn't require therapy">
                            <IconButton>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                              >
                                <g clipPath="url(#clip0_15082_365368)">
                                  <path
                                    d="M6.99984 9.33317V6.99984M6.99984 4.6665H7.00567M12.8332 6.99984C12.8332 10.2215 10.2215 12.8332 6.99984 12.8332C3.77818 12.8332 1.1665 10.2215 1.1665 6.99984C1.1665 3.77818 3.77818 1.1665 6.99984 1.1665C10.2215 1.1665 12.8332 3.77818 12.8332 6.99984Z"
                                    stroke="#B5BCC4"
                                    strokeWidth="1.33333"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </g>
                                <defs>
                                  <clipPath id="clip0_15082_365368">
                                    <rect
                                      width="14"
                                      height="14"
                                      fill="white"
                                    />
                                  </clipPath>
                                </defs>
                              </svg>
                            </IconButton>
                          </Tooltip>
                        </Typography>
                      </>
                    }
                  />
                </Grid>
              </Grid>
            </Stack>
          </Card>
          {!isMultiPayer && (
            <Card sx={{ padding: '1.5rem' }}>
              <Stack gap="1rem">
                <Typography variant="h6">
                  {action === 'Edit' ? 'Edit Payer Details' : 'Add Payer Details'}
                </Typography>
                <Grid
                  spacing={3}
                  container
                >
                  <Grid
                    item
                    xs={6}
                  >
                    <TextField
                      defaultValue={'COMMERCIAL_INSURANCE'}
                      fullWidth
                      label="Payer Type"
                      error={!!(formik.touched.payerType && formik.errors.payerType)}
                      helperText={formik.touched.payerType && (formik.errors.payerType as any)}
                      name="payerType"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      value={formik.values.payerType}
                      select
                      SelectProps={{
                        SelectDisplayProps: {
                          'data-testid': eocFormTestId('payer-type'),
                        } as HTMLAttributes<HTMLDivElement>,
                      }}
                    >
                      {getPayerPlanTypeResponse?.map((option: any) => (
                        <MenuItem
                          key={option.key}
                          value={option.displayName}
                          data-testid={eocOptionTestId(eocFormTestId('payer-type'), option.key)}
                        >
                          {option.displayName}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid
                    item
                    xs={6}
                  >
                    <TextField
                      fullWidth
                      label="Payer"
                      error={!!(formik.touched.payerPlanEntity && formik.errors.payerPlanEntity)}
                      helperText={
                        formik.touched.payerPlanEntity && (formik.errors.payerPlanEntity as any)
                      }
                      disabled={!formik.values.payerType}
                      name="payerPlanEntity"
                      onBlur={formik.handleBlur}
                      onChange={(e) => {
                        const selectedPayer = payerPlans?.find(
                          (payer: any) => payer?.payerPlanName === e.target.value
                        );
                        if (selectedPayer) {
                          setSelectedPayerPlan(selectedPayer);
                        }
                        formik.handleChange(e);
                        formik.setFieldValue('payerPlanEntity', e.target.value);
                      }}
                      value={formik.values.payerPlanEntity}
                      InputLabelProps={{
                        shrink:
                          formik.values.payerPlanEntity !== '' && !formik.touched.payerPlanEntity
                            ? true
                            : undefined,
                      }}
                      select
                      SelectProps={{
                        SelectDisplayProps: {
                          'data-testid': eocFormTestId('payer'),
                        } as HTMLAttributes<HTMLDivElement>,
                      }}
                    >
                      {getFilterPayerPlans(formik.values.payerType, payerPlans)?.length === 0 ? (
                        <Typography
                          color="text.secondary"
                          p={1}
                        >
                          No payer plan found for {formik.values.payerType}
                        </Typography>
                      ) : (
                        getFilterPayerPlans(formik.values.payerType, payerPlans)
                          ?.filter((item: any) => {
                            if (item.active) {
                              return item;
                            } else if (
                              item.payerPlanName ===
                              episodeOfCareData?.payerPlanEntity?.payerPlanName
                            ) {
                              return item;
                            }
                          })
                          ?.map((payer: any) => (
                            <MenuItem
                              key={payer?.id}
                              value={payer?.payerPlanName}
                              disabled={!payer.active}
                              data-testid={eocOptionTestId(
                                eocFormTestId('payer'),
                                payer?.uuid ?? payer?.id
                              )}
                            >
                              {payer?.payerPlanName} {!payer.active ? ' - Inactive' : ''}
                            </MenuItem>
                          ))
                      )}
                    </TextField>
                  </Grid>
                  {action === 'Edit' && (
                    <>
                      {' '}
                      <Grid
                        item
                        xs={6}
                      >
                        <DatePicker
                          onChange={(newDate) =>
                            formik.setFieldValue('payerPlanStartDate', newDate)
                          }
                          value={formik.values.payerPlanStartDate}
                          format="MM dd, yyyy"
                          sx={{
                            width: '100%',
                            border:
                              formik.touched.payerPlanStartDate && formik.errors.payerPlanStartDate
                                ? '2px solid red'
                                : '',
                            borderRadius:
                              formik.touched.payerPlanStartDate && formik.errors.payerPlanStartDate
                                ? '10px'
                                : '',
                          }}
                          label="Start Date"
                          disabled
                        />
                        {formik.touched.payerPlanStartDate && formik.errors.payerPlanStartDate && (
                          <Typography
                            padding={'0px 20px'}
                            color={'red'}
                            variant="caption"
                          >
                            {formik.errors.payerPlanStartDate}
                          </Typography>
                        )}
                      </Grid>
                      <Grid
                        item
                        xs={6}
                      >
                        <DatePicker
                          onChange={(newDate) => formik.setFieldValue('payerPlanEndDate', newDate)}
                          label="End Date"
                          value={formik.values.payerPlanEndDate}
                          minDate={formik.values.payerPlanStartDate}
                          format="MM dd, yyyy"
                          sx={{
                            width: '100%',
                            border:
                              formik.touched.payerPlanEndDate && formik.errors.payerPlanEndDate
                                ? '2px solid red'
                                : '',
                            borderRadius:
                              formik.touched.payerPlanEndDate && formik.errors.payerPlanEndDate
                                ? '10px'
                                : '',
                          }}
                          disabled={!isMultiPayer}
                        />
                        {formik.touched.payerPlanEndDate && formik.errors.payerPlanEndDate && (
                          <Typography
                            padding={'0px 16px'}
                            color={'red'}
                            variant="caption"
                          >
                            {formik.errors.payerPlanEndDate}
                          </Typography>
                        )}
                      </Grid>
                    </>
                  )}
                </Grid>
              </Stack>
            </Card>
          )}
          <Stack
            flexDirection="row"
            justifyContent="flex-end"
            gap="1rem"
          >
            <Button
              variant="text"
              onClick={() => navigate(-1)}
              data-testid={eocFormTestId('cancel')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={createSpisodOfCare?.loading || patientLoading}
              data-testid={eocFormTestId('save')}
            >
              Save
            </Button>
          </Stack>
          {(createSpisodOfCare.isCreated || editSpisodOfCare.isEdited) && (
            <SimpleSnackbar
              message={createSpisodOfCare?.response?.message || editSpisodOfCare?.response?.message}
            />
          )}
        </form>
      </Stack>
      <Modal
        open={openResidenceConfirmModal}
        onClose={() => {
          setOpenResidenceConfirmModal(false);
          setPendingResidenceEditPayload(null);
        }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '560px',
            width: '90%',
            p: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography
            variant="h6"
            sx={{ fontWeight: 600, color: '#111927' }}
          >
            Change to Place of Residence Will Reset Planning Data
          </Typography>
          <Typography sx={{ color: '#4B5563', fontSize: '14px' }}>
            Changing this patient&apos;s Place of Residence will clear all planned minutes,
            scheduled tasks, and anticipated discharge date from today forward, including any
            already-assigned sessions. This cannot be undone. You will need to replan for the new
            setting.
          </Typography>
          <Stack
            direction="row"
            spacing={1.5}
            justifyContent="flex-end"
          >
            <Button
              variant="outlined"
              onClick={() => {
                setOpenResidenceConfirmModal(false);
                setPendingResidenceEditPayload(null);
              }}
              data-testid={eocFormTestId('residence-reset-cancel')}
            >
              Go Back
            </Button>
            <Button
              variant="contained"
              color="error"
              data-testid={eocFormTestId('residence-reset-confirm')}
              onClick={() => {
                if (pendingResidenceEditPayload) {
                  dispatch(editEpisodeOfCare(pendingResidenceEditPayload));
                }
                setOpenResidenceConfirmModal(false);
                setPendingResidenceEditPayload(null);
              }}
            >
              Continue & Reset Planner
            </Button>
          </Stack>
        </Box>
      </Modal>
      <Modal
        open={openErrorModal}
        onClose={closeErrorModal}
      >
        <SucessModal
          title={
            action === 'Create'
              ? 'Episode of care could not be created!'
              : 'Episode of care could not be edited!'
          }
          message={
            action === 'Create'
              ? createSpisodOfCare?.response?.message
              : editSpisodOfCare?.response?.message
          }
          close={closeErrorModal}
          isSucess={false}
        />
      </Modal>
      <Modal
        open={openPayerConfirmationModal}
        onClose={() => {
          setOpenPayerConfirmationModal(false);
          setPendingFormValues(null);
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            backgroundColor: 'white',
            borderRadius: '12px',
            maxWidth: '625px',
            width: '90%',
            boxShadow: '0px 5px 22px 0px rgba(0, 0, 0, 0.08)',
            outline: 'none',
          }}
        >
          <PayerConfirmationModal
            close={() => {
              setOpenPayerConfirmationModal(false);
              setPendingFormValues(null);
            }}
            onConfirm={handleConfirmPayer}
            onUpdate={handleUpdatePayer}
            payerType={pendingFormValues?.hasNoPayer ? null : getPayerTypeDisplayName()}
            payerName={pendingFormValues?.hasNoPayer ? null : patientData?.payerName || ''}
            payerStartDate={
              pendingFormValues?.hasNoPayer
                ? null
                : getAllPatientPayersReducer?.data?.find((p: any) => p.isCurrent)?.startDate
            }
            payerEndDate={
              pendingFormValues?.hasNoPayer
                ? null
                : getAllPatientPayersReducer?.data?.find((p: any) => p.isCurrent)?.endDate
            }
            hasNoPayer={pendingFormValues?.hasNoPayer || false}
          />
        </Box>
      </Modal>
    </Container>
  );
};
export default AddNewEpisodeOfCare;
